import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Link2, Share2, Mail, Check } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal, Label } from "@/components/ui-bits";
import { api } from "@/lib/auth";

export function AdamShareBar({ resultId }) {
  const [copied, setCopied] = useState(false);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");
  const url = `${window.location.origin}/adam/result/${resultId}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt("Copy your result link", url);
    }
  };

  const share = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "My ADAM questionnaire result — Androgen Health", url }); } catch {}
    } else copy();
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setMsg("");
    try {
      await api(`/adam/${resultId}/email`, { method: "POST", body: { email } });
      setStatus("sent");
    } catch (err) {
      setStatus("error");
      setMsg(err.message);
    }
  };

  if (!resultId) return null;
  return (
    <div className="mt-7 border-t border-line pt-6" data-testid="adam-share-bar">
      <Label>Keep or share your result</Label>
      <div className="flex flex-wrap gap-3 mt-4">
        <button type="button" onClick={copy} className="btn-ghost !py-2.5 !px-4 text-sm gap-2" data-testid="adam-share-copy">
          {copied ? <Check size={15} className="text-teal" /> : <Link2 size={15} />} {copied ? "Link copied" : "Copy link"}
        </button>
        <button type="button" onClick={share} className="btn-ghost !py-2.5 !px-4 text-sm gap-2" data-testid="adam-share-native">
          <Share2 size={15} /> Share…
        </button>
      </div>
      {status === "sent" ? (
        <p className="text-teal text-sm mt-4" data-testid="adam-email-sent">Sent. Check your inbox for your result and a booking link.</p>
      ) : (
        <form onSubmit={sendEmail} className="flex flex-col sm:flex-row gap-3 mt-4">
          <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email me my result" className="field flex-1" data-testid="adam-email-input" />
          <button type="submit" disabled={status === "sending"} className="btn-primary !py-2.5 !px-5 text-sm gap-2 justify-center" data-testid="adam-email-submit">
            <Mail size={15} /> {status === "sending" ? "Sending…" : "Email me"}
          </button>
        </form>
      )}
      {status === "error" && <p className="text-rose text-sm mt-2" data-testid="adam-email-error">{msg}</p>}
      <p className="text-xs text-dusk mt-3 leading-relaxed">One email per result, containing only your score and interpretation. Your address is used for nothing else.</p>
    </div>
  );
}

export default function AdamSharePage() {
  const { id } = useParams();
  const [data, setData] = useState(undefined);
  if (data === undefined) {
    api(`/adam/${id}`).then(setData).catch(() => setData(null));
  }

  return (
    <>
      <Seo title="ADAM questionnaire result | Androgen Health" description="A shared ADAM (androgen deficiency) screening result with honest interpretation — a screen, not a diagnosis." />
      <PageHero label="Shared result — ADAM questionnaire" title={data ? (data.positive ? "Positive screen — a blood test is sensible." : "Negative screen — but symptoms still count.") : data === null ? "Result not found." : "Loading…"} lede={data ? `Yes to ${data.yes_count} of 10 questions · completed ${data.created_at.slice(0, 10)}` : ""} safety={false} />
      <section className="pb-24 px-5 sm:px-8" data-testid="adam-share-page">
        <div className="max-w-3xl mx-auto lg:mx-0">
          {data && (
            <Reveal>
              <div className="card-line rounded-2xl p-8 border-l-2 !border-l-teal" data-testid="adam-share-result">
                <p className="text-mute leading-relaxed">
                  {data.positive
                    ? "A positive ADAM screen means the next sensible step is a properly timed morning testosterone blood test, repeated if low. It is not a diagnosis: many men who screen positive have normal testosterone and symptoms with another cause."
                    : "The ADAM questionnaire misses some men. Persistent symptoms that affect daily life deserve a proper assessment whatever is causing them."}
                </p>
                <div className="flex flex-wrap gap-3 mt-7">
                  <Link to="/book" className="btn-primary" data-testid="adam-share-book">Book an assessment</Link>
                  <Link to="/learn/adam-questionnaire" className="btn-ghost" data-testid="adam-share-retake">Take the questionnaire</Link>
                </div>
                <AdamShareBar resultId={id} />
                <p className="text-xs text-dusk mt-6">Treatment is subject to individual clinical assessment. TRT is not suitable for everyone.</p>
              </div>
            </Reveal>
          )}
        </div>
      </section>
    </>
  );
}
